/**
 * apps/web :: pass :: screens/EmergencyCard
 *
 * Offline emergency card for the active country pack (ROADS-569 W1.C6).
 * Reads the traveler's first active `tourist_out` country (fallback "IT"),
 * resolves its pack, and renders the urgent essentials: a big "Call {number}"
 * action (e.g. 112), a GPS-coordinates placeholder line, the accident
 * `steps` (title + detail) and the bilingual `phrases` (English + local).
 *
 * Red / urgent styling so it reads as the panic screen it is. Wrapped in
 * PassLayout for consistent pass chrome.
 */
import PassLayout from "../PassLayout";
import {
  getPack,
  useActivePassCountries,
  type PackId,
} from "@/lib/pass-content";

export default function EmergencyCard(): JSX.Element {
  const country = useActivePassCountries()[0] ?? "IT";
  const pack = getPack(country as PackId);
  const { number, steps, phrases } = pack.emergency;

  return (
    <PassLayout country={country} title="Emergency card">
      {/* Primary action: call the emergency number. */}
      <a
        href={`tel:${number}`}
        data-testid="emergency-call"
        className="flex items-center justify-between rounded-xl bg-red-600 px-md py-md text-white no-underline hover:bg-red-700"
      >
        <span className="text-xl font-bold">Call {number}</span>
        <span aria-hidden="true" className="text-lg">
          ☎
        </span>
      </a>

      {/* GPS coordinates placeholder — populated on-device when available. */}
      <p
        data-testid="emergency-gps"
        className="mt-md rounded-md border border-neutral-200 px-md py-sm text-sm text-neutral-600"
      >
        <span className="font-semibold text-neutral-800">Your location: </span>
        Locating… read your GPS coordinates to the operator.
      </p>

      {/* Accident steps. */}
      <section className="mt-lg" data-testid="emergency-steps">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-red-600">
          If you have an accident
        </h2>
        <ol className="mt-sm flex flex-col gap-2">
          {steps.map((step, i) => (
            <li
              key={step.title}
              data-testid={`emergency-step-${i}`}
              className="rounded-md border border-red-100 bg-red-50 px-md py-sm"
            >
              <p className="text-sm font-semibold text-red-900">{step.title}</p>
              <p className="mt-0.5 text-sm text-red-800/90">{step.detail}</p>
            </li>
          ))}
        </ol>
      </section>

      {/* Bilingual phrases. */}
      <section className="mt-lg" data-testid="emergency-phrases">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-neutral-500">
          Useful phrases
        </h2>
        <ul className="mt-sm flex flex-col gap-2">
          {phrases.map((phrase, i) => (
            <li
              key={phrase.en}
              data-testid={`emergency-phrase-${i}`}
              className="rounded-md border border-neutral-200 px-md py-sm"
            >
              <p className="text-sm font-semibold text-neutral-900">
                {phrase.local}
              </p>
              <p className="mt-0.5 text-xs text-neutral-500">{phrase.en}</p>
            </li>
          ))}
        </ul>
      </section>
    </PassLayout>
  );
}
